/* eslint-disable */
import type { Money } from "../common/currency.pb";
import type { GeoLocation, GlobalAddress } from "../common/location.pb";
import type { BrowserInteraction } from "../franklin/browser_interaction.pb";
import type { IdentityVerificationResultMatchResult } from "../franklin/identity.pb";
import type { Merchant as Merchant1, MerchantAttributeSource } from "../merchantein/merchant/merchant.pb";
import type { CardSource, DeviceType, DigitalWalletIssuer, DigitalWalletState } from "./digital_wallet.pb";
import type { ExternalSystem } from "./external/external_system.pb";
import type { CardType, IssuedCardBrand } from "./issued_card.pb";
import type { MovementReason } from "./movement_reason.pb";
import type { IssuedInstrumentVariant, TransactionEvent, TransactionReasonCode } from "./transaction.pb";

export enum Immediacy {
  /** ACTIONABLE - This is a current request to be processed right now. */
  ACTIONABLE = "ACTIONABLE",
  /** ADVICE - This is an advice of a request that has already happened and MUST be honored. */
  ADVICE = "ADVICE",
  /** NOTICE - This is a notice of a request that has already happened, but does not need to be honored. */
  NOTICE = "NOTICE",
}

export enum RuleType {
  THROTTLE = "THROTTLE",
  ORDER = "ORDER",
  COST = "COST",
}

export enum Network {
  ALL = "ALL",
  MAESTRO = "MAESTRO",
  PULSE = "PULSE",
  STAR = "STAR",
  SHAZAM = "SHAZAM",
  NYCE = "NYCE",
  VISA_NETWORK = "VISA_NETWORK",
  MASTER_CARD = "MASTER_CARD",
}

export enum BinType {
  REGULATED = "REGULATED",
  UNREGULATED = "UNREGULATED",
}

export enum FeeType {
  FIXED = "FIXED",
  VARIABLE = "VARIABLE",
}

/** KYC state for virtual card. */
export enum VirtualCardKycState {
  /** KYC_SUCCESS - All verification fields MATCHED. */
  KYC_SUCCESS = "KYC_SUCCESS",
  /** KYC_PARTIAL_MATCH - Only received partial match. Customer is verified for GOLD but not for virtual card. */
  KYC_PARTIAL_MATCH = "KYC_PARTIAL_MATCH",
  /** KYC_FAILED - KYC Failed. */
  KYC_FAILED = "KYC_FAILED",
  /** KYC_ATTEMPTS_REMAINING - KYC has not been successful yet, but the customer still has attempts remaining. */
  KYC_ATTEMPTS_REMAINING = "KYC_ATTEMPTS_REMAINING",
}

/** TODO(tmcfarlin): KILL THIS. Replace with card_theme_token. */
export enum CardTheme {
  BLACK = "BLACK",
  WHITE = "WHITE",
}

/** Why Banklin can or can't forward a stored value balance */
export enum VerifiedForwardResult {
  VALID = "VALID",
  DDA_BALANCE_LIMIT_EXCEEDED = "DDA_BALANCE_LIMIT_EXCEEDED",
  CANT_FORWARD_TWO_STORED_VALUES_WITH_DDAS = "CANT_FORWARD_TWO_STORED_VALUES_WITH_DDAS",
}

export enum FulfillmentStatus {
  /** CARD_SHIPPED - Card was shipped successfully. */
  CARD_SHIPPED = "CARD_SHIPPED",
  /** CARD_CANCELLED - Cancelling a job is always approved by Square beforehand */
  CARD_CANCELLED = "CARD_CANCELLED",
  /** CARD_REJECTED - A card customization was rejected. */
  CARD_REJECTED = "CARD_REJECTED",
  /** CARD_DESTROYED - This would be if Square requested the card to be destroyed. */
  CARD_DESTROYED = "CARD_DESTROYED",
}

export enum CardShippingMethod {
  USPS = "USPS",
  /** FEDEX_FIRST_OVERNIGHT - Following are for bulk orders from Perfect Plastic and shipped via Fedex. */
  FEDEX_FIRST_OVERNIGHT = "FEDEX_FIRST_OVERNIGHT",
  FEDEX_STANDARD_OVERNIGHT = "FEDEX_STANDARD_OVERNIGHT",
  FEDEX_PRIORITY_OVERNIGHT = "FEDEX_PRIORITY_OVERNIGHT",
  FEDEX_TWO_DAY = "FEDEX_TWO_DAY",
  FEDEX_EXPRESS_SAVER = "FEDEX_EXPRESS_SAVER",
  FEDEX_GROUND = "FEDEX_GROUND",
  USPS_FIRST_CLASS = "USPS_FIRST_CLASS",
  /** USPS_PACKAGE_SERVICES - Used for metal card */
  USPS_PACKAGE_SERVICES = "USPS_PACKAGE_SERVICES",
}

export enum CardAction {
  TERMINATE = "TERMINATE",
  ENABLE_ENHANCED_MONITORING = "ENABLE_ENHANCED_MONITORING",
  /** NO_ACTION - Take no action on the card */
  NO_ACTION = "NO_ACTION",
}

export interface PurchaseContract {
  token?: string;
  source_amount?: Money;
  target_amount?: Money;
  expires_at?: number;
  spreads?: PurchaseContractSpread[];
  executed_at?: number;
  fee_cents?: number;
}

export interface PurchaseContractSpread {
  spread_bps?: number;
  source?: PurchaseContractSpreadSource;
}

export enum PurchaseContractSpreadSource {
  BITBANK = "BITBANK",
  BANKLIN = "BANKLIN",
}

export interface MerchantExtraInfo {
  /** Unique identifier generated by banklin */
  token?: string;
  /** Current status of this suggestion */
  state?: MerchantExtraInfoState;
  /** Source used to create record */
  source?: MerchantAttributeSource;
  /** Suggested name of merchant */
  canonical_name?: string;
  /** Levenshtein Distance between squareup.merchantein.merchant.Merchant name and suggested name */
  levenshtein_distance_for_name?: number;
  /** Suggested Url (only the host part) of the merchant */
  canonical_url?: string;
}

export enum MerchantExtraInfoState {
  NEW = "NEW",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
}

/**
 * This is for hackweek 2019
 * TODO(mzhou) remove after hackweek 2019
 */
export interface Merchant {
  /** @deprecated */
  longtitude?: number;
  /** @deprecated */
  latitude?: number;
  start_time?: number;
  end_time?: number;
  distance?: number;
  location?: Location;
  merchant_name?: string;
}

export interface Location {
  latitude?: number;
  longitude?: number;
}

export interface TopMerchantResult {
  merchant?: Merchant1;
  transaction_count?: number;
}

export interface MerchantInfoSuggestionResult {
  merchant?: Merchant1;
  extra_infos?: MerchantExtraInfo[];
}

export interface BizbankTransactionEvent {
  transaction_event?: TransactionEvent;
}

export interface ProblemTransaction {
  /** Unique token representing this transaction. */
  token?: string;
  /** Unique token representing the card. */
  issued_card_token?: string;
  /** Affected cash stored value balance */
  stored_value_token?: string;
  /** Unique token representing the merchant. */
  merchant_token?: string;
  /** Square curated name for this merchant or store. */
  canonicalName?: string;
  /** Merchant category code */
  mcc?: string;
  /** Type of the problem transaction */
  type?: string;
  /** The originating reason for this transaction. Example: ATM Withdrawal. */
  reason_code?: string;
}

export interface RiskScore {
  /** scored by shazam for instance */
  risk_score?: number;
  /** Who originated the risk score */
  risk_system?: ExternalSystem;
  assessment?: string;
  reason_code?: string;
}

/**
 * Request object, analogous to BalanceModification that deals with
 * Apple Pay and Android Pay tokenization.
 */
export interface TokenActivation {
  /** opaque reference to the actual apple-pay token */
  issuer_token?: string;
  /** i.e. the card token itself */
  instrument_token?: string;
  /** Apple / Google device id, if any */
  device_id?: string;
  /** Device that originated the tokenization */
  device_type?: DeviceType;
  /** The state in which the digital wallet token currently is */
  state?: DigitalWalletState;
  external_system?: ExternalSystem;
  /** Only Yellow-flow and suspended token requests are actionable */
  immediacy?: Immediacy;
  /** Example: If a Mobile phone, the number attached to it */
  phone_number?: string;
  ip_address?: string;
  /** Risk assessment by external partner (e.g. Apple). May be DECISION_YELLOW, etc. */
  risk_score?: RiskScore;
  geo_location?: GeoLocation;
  address_verification?: GlobalAddress;
  /** Example: Dhanji's iPhone */
  device_name?: string;
  /** When was this DWT activation request made? */
  requested_at?: number;
  /** How this token was generated */
  card_source?: CardSource;
  /** Whether Apple Pay or Android */
  digital_wallet_issuer?: DigitalWalletIssuer;
  /** Apple Pay or Android Pay, etc. */
  reason_code?: TransactionReasonCode;
  /** The vector along which the issued instrument was provisioned for apple pay */
  instrument_variant?: IssuedInstrumentVariant;
  /** The FPAN Identifier for Apple */
  pan_reference_id?: string;
  /** The DPAN Identifier for Apple */
  device_pan_id?: string;
  /** The score from the device. Provided by the digital wallet * */
  device_score?: number;
  /** Token service provider requestor name. Ex: PAYPAL, ANDROID_PAY,... * */
  dwt_token_requestor_name?: string;
  /** Token service provider requestor ID. * */
  dwt_token_requestor_id?: string;
  /** Token service provider token type * */
  token_type?: string;
  /** Wallet provider profile account ID * */
  account_id?: string;
  /** Wallet provider profile account email address token * */
  account_email_address?: string;
}

/** TODO(dhanji): Add instrument that created modification attempt */
export interface BalanceModification {
  /** Event that triggered the potential balance modification */
  event?: TransactionEvent;
  /** Affected cash stored value balance */
  stored_value_token?: string;
  /** Affected stored value balances and how much to change each */
  allocations?: BalanceModificationAllocation[];
  risk_scores?: RiskScore[];
  /** Gateway-specific information */
  external_system_data?: BalanceModificationExternalSystemData;
  /** Whether or not this is a current actionable request, advice or notice. */
  immediacy?: Immediacy;
  /** Origination reason for this transaction, like "ONLINE_PURCHASE". */
  reason_code?: TransactionReasonCode;
  /** The payment vector used for the associated instrument */
  instrument_variant?: IssuedInstrumentVariant;
  /** Supplementing events such as an acquirer fee (ex. ATM fee) */
  supplementing_events?: TransactionEvent[];
  flags?: BalanceModificationFlags;
  /**
   * If present, represents a reference to a previous transaction event that this
   *  BalanceModification is modifying.  The reference may be any of the transaction events
   *  within a transaction chain, it is not required to be the last (most recent) event.
   */
  transaction_event_reference?: BalanceModificationTransactionEventReference;
  supplemental_reconciliation_data?: SupplementalReconciliationData;
  customer_token?: string;
  /** If true then ledger can perform a partial auth if customer does not have enough balance for a full auth */
  allow_partial_auth?: boolean;
}

/**
 * Instructions for how to augment a storedValueBalance
 * NOTE: This is experimental / early development with potential to break.
 * Reach out to #cash-finplat-treasury before using this.
 */
export interface BalanceModificationAllocation {
  /**
   * Amount to change the balance.
   * Positive amount will increase the balance. Negative amount will decrease the balance.
   * Banklin validates the presence of this field to prevent callers from leaving it blank.
   */
  amount?: Money;
  /** The token of the SVB to increment or decrement */
  stored_value_balance_token?: string;
  /**
   * Version of the storedValueBalance prior to applying the modification.
   * Populate this field only if greater balance consistency is desired.
   * If the stored_value_balance_version for any Allocation does not match the version in banklin db,
   * then banklin will not ledger anything and will respond with updated balance data.
   */
  stored_value_balance_version?: number;
}

export interface BalanceModificationExternalSystemData {
  /** Whether a CNP or CP etc. */
  card_presence?: BalanceModificationExternalSystemDataCardPresence;
  /** Some free text that may be set by the gateway or during JIT funding by us */
  memo?: string;
  /** Information about the merchant, if any. */
  merchant?: Merchant1;
  ach_data?: BalanceModificationExternalSystemDataAchData | undefined;
  plastic_data?: BalanceModificationExternalSystemDataPlasticData | undefined;
  marqeta_data?: BalanceModificationExternalSystemDataMarqetaData | undefined;
  cash_data?: BalanceModificationExternalSystemDataCashData | undefined;
  bitcoin_data?: BalanceModificationExternalSystemDataBitcoinData | undefined;
}

export enum BalanceModificationExternalSystemDataCardPresence {
  UNKNOWN = "UNKNOWN",
  CARD_PRESENT = "CARD_PRESENT",
  CARD_NOT_PRESENT = "CARD_NOT_PRESENT",
  CARD_NOT_PRESENT_ONLINE = "CARD_NOT_PRESENT_ONLINE",
}

export interface BalanceModificationExternalSystemDataAchData {
  transaction_code?: number;
  amount?: string;
  company_discretionary_data?: string;
  company_entry_description?: string;
  company_id?: string;
  company_name?: string;
  customer_id?: string;
  customer_name?: string;
  discretionary_data?: string;
  effective_entry_date?: string;
  originating_dfiid?: string;
  originator_id?: string;
  originator_status_code?: number;
  receiving_dfiid?: string;
  service_class_code?: number;
  settlement_date?: number;
  standard_entry_class_code?: string;
  trace_number?: string;
}

export interface BalanceModificationExternalSystemDataPlasticData {
  /** Information about the acquirer, if available. (SHAZAM) */
  acquirer_identification_code?: string;
  /** Information about the receiving institution, if available. (SHAZAM) */
  receiving_institution_identification_code?: string;
  /** Number of minutes before we should expire an uncompleted auth. (SHAZAM) */
  authorization_life_cycle_minutes?: number;
}

export interface BalanceModificationExternalSystemDataMarqetaData {
  /** The Marqeta type for this transaction (see MarqetaApi.java) */
  transaction_type?: string;
  /** The Marqeta state for this transaction (see MarqetaApi.java) */
  transaction_state?: string;
  /**
   * The id of the previous Marqeta transaction that this one is about,
   * example: Capture of a prior Authorization
   */
  preceding_related_transaction_token?: string;
  /** Contains fees such as ATM fee. */
  acquirer_fee?: Money;
  /** the network that the transaction was sent by ie Mastercard if applicable */
  network?: string;
  /** The transaction token for Marqeta GPA order */
  gpa_order_transaction_token?: string;
}

export interface BalanceModificationExternalSystemDataCashData {
  movement_reason?: MovementReason;
}

export interface BalanceModificationExternalSystemDataBitcoinData {
  wallet_address?: string;
  blockchain_transaction_id?: string;
}

/** Feature flags that change how the event is processed */
export interface BalanceModificationFlags {
  enhanced_monitoring_enabled?: boolean;
  impacted_by_load_shed?: boolean;
}

export interface BalanceModificationTransactionEventReference {
  external_system?: ExternalSystem;
  external_system_id?: string;
}

export interface BinMechanismStats {
  total_stats?: BinMechanismStatsStat[];
  bin_stats?: BinMechanismStatsBinStat[];
}

export interface BinMechanismStatsStat {
  mechanism?: string;
  count?: number;
  fail_count?: number;
  fail_rate?: number;
  fail_weight?: number;
}

export interface BinMechanismStatsBinStat {
  bin?: string;
  stats?: BinMechanismStatsStat[];
}

export interface ExperimentEvent {
  id?: string;
  experiment_id?: string;
  experiment_override_id?: string;
  type?: ExperimentEventType;
  experiment_name?: string;
  buckets?: string;
  measured?: boolean;
  identifier?: string;
  /** Unix time in millis. */
  created_at?: number;
  /** Unix time in millis. */
  updated_at?: number;
}

export enum ExperimentEventType {
  EXPERIMENT = "EXPERIMENT",
  OVERRIDE = "OVERRIDE",
}

export interface StarBin {
  id?: string;
  sequence_no?: string;
  bin_length?: number;
  bin?: string;
  min_pan_length?: number;
  max_pan_length?: number;
  bin_start_position?: number;
  product_network_id?: string;
  atm_pos_interchange_group?: string;
  us_debit_aid?: string;
  token_indicator?: string;
  pan_country_code?: string;
  state_indicator?: string;
}

export interface SquareEmployee {
  customer_token?: string;
  cash_employee?: boolean;
  username?: string;
  state?: SquareEmployeeState;
}

export enum SquareEmployeeState {
  ENABLED = "ENABLED",
  /** DISABLED - For Cash's purposes we treat terminated as disabled. */
  DISABLED = "DISABLED",
  /** FORCED - Forced is the same as enabled except the state will never be updated. */
  FORCED = "FORCED",
}

export interface Tag {
  name?: string;
  priority?: number;
}

export interface CardCustomizationData {
  stored_value_token?: string;
  customization_token?: string;
  trunk_token?: string;
  parent_issued_card_token?: string;
  issued_card_token?: string;
  card_issuer?: string;
  signature_upload_token?: string;
  signature_bytes?: Uint8Array;
  /** @deprecated */
  file_name?: string;
  /** @deprecated */
  card_theme?: CardTheme;
  card_theme_token?: string;
  signature_uploaded_at?: number;
  imb_number?: string;
  card_payment_method?: string;
}

export interface CardFeatures {
  ddaFeature?: CardFeaturesDdaFeature;
  /** Consumer or commercial account */
  account_type?: CardFeaturesAccountType;
  card_brand?: IssuedCardBrand;
  card_type?: CardType;
  /** @deprecated */
  card_theme?: CardTheme;
  card_theme_token?: string;
  printing_technology?: CardFeaturesPrintingTechnology;
  card_product_token?: string;
  fulfiller?: string;
  is_direct_order?: string;
  payment_product_token?: string;
  payment_product_spec_token?: string;
  external_product_configuration_token?: string;
  external_product_package_id?: string;
}

export enum CardFeaturesDdaFeature {
  /** REQUIRED - The customer wants a DDA */
  REQUIRED = "REQUIRED",
  /** NOT_REQUIRED - DDA for the customer is not required but can still be provided. */
  NOT_REQUIRED = "NOT_REQUIRED",
}

export enum CardFeaturesAccountType {
  CONSUMER = "CONSUMER",
  COMMERCIAL = "COMMERCIAL",
}

/** Physical card printing technology */
export enum CardFeaturesPrintingTechnology {
  /** LASER - Laser printing technology (supports cropped customization image) */
  LASER = "LASER",
  /** DOD - Drop on demand printing technology (supports full card customization image) */
  DOD = "DOD",
}

export interface CardFulfillmentSummary {
  customization_data?: CardCustomizationData;
  /** Date of shipment, time will always be midnight */
  shipped_at?: number;
  shipping_method?: CardShippingMethod;
  /** Tracking number for bulk shipped items */
  tracking_number?: string;
  status?: FulfillmentStatus;
  /** This will only be present if card status is REJECTED */
  reject_reason?: string;
  /** IMB number used for tracking card shipments */
  imb_number?: string;
}

export interface AutoReloadPreference {
  stored_value_token?: string;
  enabled?: boolean;
  threshold_cents?: number;
  reload_amount_cents?: number;
}

export interface BankAccountEvent {
  bank_account_token?: string;
  stored_value_token?: string;
  type?: BankAccountEventType;
  result?: BankAccountEventResult;
  message?: string;
  occurred_at?: number;
}

export enum BankAccountEventType {
  OPEN = "OPEN",
  UPLOAD_DRIVERS_LICENSE = "UPLOAD_DRIVERS_LICENSE",
  UPLOAD_UTILITY_BILL = "UPLOAD_UTILITY_BILL",
  UPLOAD_BANK_STATEMENT = "UPLOAD_BANK_STATEMENT",
  UPLOAD_VERIFICATION_SUMMARY = "UPLOAD_VERIFICATION_SUMMARY",
  UPDATE_CUSTOMER = "UPDATE_CUSTOMER",
  LOCKED_CUSTOMER_ACCOUNT = "LOCKED_CUSTOMER_ACCOUNT",
  UNLOCKED_CUSTOMER_ACCOUNT = "UNLOCKED_CUSTOMER_ACCOUNT",
  CLOSED_CUSTOMER_ACCOUNT = "CLOSED_CUSTOMER_ACCOUNT",
  CUSTOMER_CLOSED = "CUSTOMER_CLOSED",
}

export enum BankAccountEventResult {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
  ISSUER_EXCEPTION = "ISSUER_EXCEPTION",
  INTERNAL_EXCEPTION = "INTERNAL_EXCEPTION",
}

export interface DdaIssuanceAttempt {
  token?: string;
  dda_token?: string;
  occurred_at?: number;
  customer_token?: string;
  ssn_token?: string;
  reason?: DdaIssuanceAttemptReason;
  browser_interaction?: BrowserInteraction;
  /** @deprecated */
  idv_address_result?: IdentityVerificationResultMatchResult;
  eligibility_failure?: string;
  email_address?: string;
  email_domain?: string;
  issued_account_fidelius_token?: string;
  ciphertext_?: Uint8Array;
}

export enum DdaIssuanceAttemptReason {
  CASH_CARD_ONBOARDING = "CASH_CARD_ONBOARDING",
  PHYSICAL_CASH_CARD_ACTIVATION = "PHYSICAL_CASH_CARD_ACTIVATION",
  CASH_TAX_RETURN = "CASH_TAX_RETURN",
}

export enum DdaIssuanceAttemptEligibilityFailure {
  NO_VERIFIED_ADDRESS = "NO_VERIFIED_ADDRESS",
  SPONSORED_CUSTOMER = "SPONSORED_CUSTOMER",
  RISK_DENY = "RISK_DENY",
  RISK_REQUIRE_CARD_ACTIVATION = "RISK_REQUIRE_CARD_ACTIVATION",
  RISK_REQUIRE_IDV = "RISK_REQUIRE_IDV",
  RISK_LOCK_ACCOUNT = "RISK_LOCK_ACCOUNT",
  FEATURE_NOT_ENABLED = "FEATURE_NOT_ENABLED",
  SERVER_ERROR = "SERVER_ERROR",
}

export interface DdaIssuanceEligibilityCheck {
  token?: string;
  request_token?: string;
  occurred_at?: number;
  customer_token?: string;
  ssn_token?: string;
  reason?: DdaIssuanceAttemptReason;
  eligibility_failure?: string;
  ciphertext_?: Uint8Array;
}

export interface FundSettlement {
  start_time?: number;
  end_time?: number;
  source_account?: string;
  target_account?: string;
  amount_cents?: number;
  state?: FundSettlementState;
  data?: FundSettlementFundSettlementData;
  /** This is optional, it can be used in lieu of date overlap idempotence logic. */
  idempotence_token?: string;
  /** This is used to separate fund movements for the same date range and same accounts between different settlement jobs. */
  scope?: FundSettlementFundSettlementScope;
}

export enum FundSettlementState {
  PENDING = "PENDING",
  ERROR = "ERROR",
  COMPLETED = "COMPLETED",
}

export enum FundSettlementFundSettlementScope {
  UNDEFINED = "UNDEFINED",
  LENDING = "LENDING",
  /** MARQETA - identifies money movement associated with marqeta program (excluding bizbank) */
  MARQETA = "MARQETA",
}

/**
 * This can't be named "Data" because Frisky compiles this using protoc, which wraps this proto
 * file in a Data class (since this file's name is "data.proto").  If a nested message is named
 * "Data", protoc names the outer class "OuterDataClass" which messes up the imports throughout
 * frisky.
 */
export interface FundSettlementFundSettlementData {
  exception?: string;
  summary?: string;
}

export interface FileUpload {
  name?: string;
  /** @deprecated */
  data?: string;
  file_data?: Uint8Array;
}

export interface ChargebackCase {
  transaction_token?: string;
  case_open_date?: string;
  claim_type?: ChargebackCaseClaimType;
  reason?: string;
  card_status?: ChargebackCaseCardStatus;
  card_state?: ChargebackCaseCardState;
  customer_message?: string;
  files?: FileUpload[];
}

export enum ChargebackCaseClaimType {
  REG_E = "REG_E",
  SQ_ERROR = "SQ_ERROR",
  SERVICE = "SERVICE",
}

export enum ChargebackCaseCardStatus {
  IN_POSSESSION = "IN_POSSESSION",
  DEACTIVATED_AND_REISSUED = "DEACTIVATED_AND_REISSUED",
}

export enum ChargebackCaseCardState {
  ACTIVE = "ACTIVE",
  DEACTIVATED = "DEACTIVATED",
}

/**
 * Based on: https://docs.google.com/document/d/1cBhYU0K6sFeWpUYG90nCUp93S1BLvxIFCGoMZes4ShI/edit#
 *
 * A record of a crypto money-related event that may be interesting for accounting, reconciliation,
 * reporting, support, or debugging.
 */
export interface CryptoMoneyMovementEvent {
  /** A unique id generated for the event */
  id?: string;
  /**
   * The token for the transaction in Banklin that contained the crypto money movement transaction
   * events
   */
  transaction_token?: string;
  /**
   * The token for transaction event that moved money and caused the crypto money movement event to
   * be put in the reconciliation feed.
   */
  transaction_event_token?: string;
  /**
   * A currency exchange is modelled as two transactions in Banklin a buy and a sell. For example a
   * customer buying bitcoin would generate both a buy and a sell event. Use the this field to pair
   * the two transactions together
   */
  transaction_reference?: string;
  /** The timestamp for when the transaction event was processed by Banklin, Unix time in millis. */
  created_at?: number;
  /** The party originating the transaction */
  originator?: CryptoMoneyMovementEventOriginator;
  /** The amount of money moving in the event */
  amount?: Money;
  /** The transaction action from the dealt balances perspective. */
  action?: CryptoMoneyMovementEventAction;
  /** The balance that is taking the action */
  dealt_balance_token?: string;
  /** The balance the action is being settled from */
  settlement_balance_token?: string;
  /** The square cash customer token */
  customer_token?: string;
  /** The ID of the blockchain transaction, only filled in for transactions that hit the blockchain */
  blockchain_transaction_id?: string;
  /** The amount of currency exchanged for the money moving in the event */
  exchange_amount?: Money;
}

export enum CryptoMoneyMovementEventOriginator {
  CUSTOMER = "CUSTOMER",
  SQUARE_TREASURY = "SQUARE_TREASURY",
  SQUARE_COMPLIANCE = "SQUARE_COMPLIANCE",
}

export enum CryptoMoneyMovementEventAction {
  BUY = "BUY",
  SELL = "SELL",
  DEPOSIT = "DEPOSIT",
  WITHDRAW = "WITHDRAW",
  DECLINE_DEPOSIT = "DECLINE_DEPOSIT",
}

export interface RewardDiscountEvent {
  token?: string;
  transaction_token?: string;
  selected_reward_token?: string;
  reward_token?: string;
  potential_reward_applied?: RewardDiscountEventPotentialRewardApplied | undefined;
  actual_reward_applied?: RewardDiscountEventActualRewardApplied | undefined;
  reward_refunded?: RewardDiscountEventRewardRefunded | undefined;
  customer_token?: string;
}

/**
 * An event when reward is being applied to transaction authorization. Actual reward execution
 * will happen on transactions settle based on the settlement amount.
 */
export interface RewardDiscountEventPotentialRewardApplied {
  /**
   * Absolute discount amount that was given in cents on authorization.
   * This is in the card txn's currency.
   */
  amount?: Money;
  /**
   * Absolute reward amount given in the boost currency on auth, populated if boost is in a
   * different currency than the card txn.
   */
  reward_amount?: Money;
  /** The entire absolute transaction amount upon authorization (including the discounted amount) */
  transaction_amount?: Money;
  /** the unix timestamp of the transaction pending event */
  transaction_pending_at?: number;
}

/** An event when reward is being applied to transaction settlement. */
export interface RewardDiscountEventActualRewardApplied {
  /** absolute discount that was given in cents on settlement. */
  amount?: Money;
  /** the entire absolute transaction amount upon settlement (including the discounted amount) */
  transaction_amount?: Money;
}

/** If transaction is refunded by the merchant, some or all reward discount may also be refunded. */
export interface RewardDiscountEventRewardRefunded {
  /** absolute discount that was refunded in cents. */
  amount?: Money;
}

export interface RewardEvaluationEvent {
  transaction_token?: string;
  occurred_at?: number;
  eligible?: RewardEvaluationEventEligible | undefined;
  ineligible?: RewardEvaluationEventIneligible | undefined;
}

export interface RewardEvaluationEventEligible {
}

export interface RewardEvaluationEventIneligible {
  global_boost_rate_limited?: RewardEvaluationEventIneligibleGlobalBoostRateLimited | undefined;
  transaction_less_than_minimum?: RewardEvaluationEventIneligibleTransactionLessThanMinimum | undefined;
  ineligible_transaction_reason_code?: RewardEvaluationEventIneligibleIneligibleTransactionReasonCode | undefined;
  no_active_selected_rewards?: RewardEvaluationEventIneligibleNoActiveSelectedRewards | undefined;
}

export interface RewardEvaluationEventIneligibleGlobalBoostRateLimited {
  selected_reward_token?: string;
  reward_token?: string;
  rate_limit_period_ms?: number;
}

export interface RewardEvaluationEventIneligibleTransactionLessThanMinimum {
  selected_reward_token?: string;
  reward_token?: string;
}

export interface RewardEvaluationEventIneligibleIneligibleTransactionReasonCode {
}

export interface RewardEvaluationEventIneligibleNoActiveSelectedRewards {
}

export interface EquityOrder {
  symbol?: string;
  amount?: number;
  side?: EquityOrderSide;
  type?: EquityOrderType;
  equity_order_token?: string;
  client_order_id?: string;
  created_at?: number;
  updated_at?: number;
}

export enum EquityOrderSide {
  BUY = "BUY",
  SELL = "SELL",
}

export enum EquityOrderType {
  MARKET = "MARKET",
  LIMIT = "LIMIT",
}

export interface VelocityControl {
  token?: string;
  name?: string;
  amount_limit?: number;
  uses?: number;
  daysRemaining?: number;
}

export interface EquityPurchaseContract {
  symbol?: string;
  side?: EquityOrderSide;
  price_cents?: number;
  equity_purchase_contract_token?: string;
  expires_at?: number;
  /**
   * Equity purchase contracts are always defined as having dealt shares.  As we are using
   * fractional shares, this will always contain a fractional amount of shares bought or sold.
   */
  dealt_amount?: number;
  /**
   * Equity purchase contracts are always defined as having settled currency.  This has the result
   * that, if using USD, this will always contain an USD amount in cents.
   */
  settlement_amount?: number;
  is_executed?: boolean;
  equity_account_id?: string;
  dealt_token?: string;
  settlement_token?: string;
}

/** @deprecated */
export interface CustomerStoredValueBalanceEvent {
  stored_value_balance_token?: string;
  balance_created?: CustomerStoredValueBalanceEventBalanceCreated | undefined;
  balance_forwarded?: CustomerStoredValueBalanceEventBalanceForwarded | undefined;
}

/** This event is published when a balance is created for a customer. */
export interface CustomerStoredValueBalanceEventBalanceCreated {
  customer_token?: string;
}

/** This event is published when a balance is forwarded to another balance. */
export interface CustomerStoredValueBalanceEventBalanceForwarded {
  target_balance_token?: string;
}

/**
 * TODO(worstell): Remove after backfill
 * An inactive customer that should point to a different active customer (and the
 * active customer it should point to)
 */
export interface InactiveCustomerAndActiveCustomer {
  inactive_customer_token?: string;
  active_customer_token?: string;
}

export interface Disclosures {
  political_exposure?: DisclosuresPoliticalExposure;
  control_person?: DisclosuresControlPerson;
  firm_affiliation?: DisclosuresFirmAffiliation;
}

export interface DisclosuresPoliticalExposure {
  organization?: string;
  family?: string[];
}

export interface DisclosuresControlPerson {
  company_symbols?: string;
}

export interface DisclosuresFirmAffiliation {
  name?: string;
}

export interface Documents {
  political_exposure?: DocumentsPoliticalExposure;
}

export interface DocumentsPoliticalExposure {
  file_id?: string;
  type?: string;
}

export interface Visa {
  type?: VisaType;
  expiration?: number;
}

export enum VisaType {
  B1 = "B1",
  C1 = "C1",
  E1 = "E1",
  E2 = "E2",
  E3 = "E3",
  F1 = "F1",
  H1B = "H1B",
  H2B = "H2B",
  H3 = "H3",
  K1 = "K1",
  L1 = "L1",
}

/** Additional data that helps Recon identify a transaction associated with a BalanceModification. */
export interface SupplementalReconciliationData {
  /**
   * The transaction ID from SHAZAM. This is unique to the transaction but not the particular event.
   * This helps recon disambiguate which transaction is being referenced since none of the other
   * values they pass are consistent or unique enough.
   */
  shazam_transaction_id?: string;
}
